// Boiler Plate Imports
import React, { useEffect, useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  remove as removeNotification,
  add as addNotification,
} from "../../redux-store/notificationsSlice";
import { update as setBreadCrumbs } from "../../redux-store/breadCrumbsSlice";
import API from "../../api";
import colors from "../../theme/colors";
import { FullPageFormWrapper } from "../../components/Forms/shared";

// Conatiner Component Imports
import BreadCrumbs from "../../components/Navigation/breadcrumbs";
import ModalComponent from "../../components/Modal";

// Page Component Imports
import AdminNetworkForm from "../../components/Forms/adminNetworkForm";
import { useNavigate, useParams } from "react-router-dom";
import ConfirmForm from "../../components/Forms/confirmForm";
import { NoChartDataPlaceholder } from "../../components/NoDataPlaceholder";
import { Button, Card, CircularProgress } from "@mui/material";
import MetricBoxesComponent from "../../components/Page/metricBoxes";
import styled from "styled-components";
import TableHeader from "../../components/Page/tableHeader";
import TableSubHeader from "../../components/Page/tableSubHeader";
import {
  ItemLink,
  StatusTag,
  TableComponent,
} from "../../components/Table/dataGridPro";
import { FormatPhoneNumber } from "../../components/PhoneNumberInput";

const PaddingWrapper = styled.div`
  padding: 0 15px 10px;
  @media (max-width: 1080px) {
    padding: 0 0px 10px;
  }
`;

const Table = React.memo(TableComponent);

const NetworkDashboard = () => {
  const { networkId } = useParams();

  const [network, setNetwork] = useState({});
  const [metrics, setMetrics] = useState([]);
  const [reloadData, setReloadData] = useState(false);
  const [loading, setLoading] = useState(true);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const tableColumns = useMemo(
    () => [
      {
        headerName: "Organization Name",
        field: "name",
        valueGetter: (value, row) => {
          return row.name;
        },
        renderCell: ({ row }) => {
          return (
            <ItemLink to={`/${row.id}/organization/`}>{row.name}</ItemLink>
          );
        },
      },
      {
        headerName: "Active Alarm",
        field: "activeAlarmsCount",
        type: "number",
        display: "flex",
        valueGetter: (value, row) => {
          return row.activeAlarmsCount;
        },
        renderCell: ({ row }) => {
          if (row.activeAlarmsCount === 0) {
            return <StatusTag>none</StatusTag>;
          } else {
            return (
              <StatusTag className={"alarm"}>{row.activeAlarmsCount}</StatusTag>
            );
          }
        },
      },
      {
        headerName: "Total Locations",
        field: "locationsCount",
        type: "number",
      },
      {
        headerName: "Total Tanks",
        field: "tanksCount",
        type: "number",
      },
      {
        headerName: "Total Users",
        field: "usersCount",
        type: "number",
      },
      {
        headerName: "Date Created",
        field: "created",
        type: "dateTime",
        valueGetter: (value, row) => {
          return new Date(value);
        },
      },
    ],
    []
  );

  const userTableColumns = useMemo(
    () => [
      {
        headerName: "User Name",
        field: "name",
        type: "string",
        valueGetter: (value, row) => {
          return `${row.firstName} ${row.lastName}`;
        },
        renderCell: ({ row }) => {
          return (
            <ItemLink
              to={`/admin/users/${row.id}/edit`}
            >{`${row.firstName} ${row.lastName}`}</ItemLink>
          );
        },
      },
      {
        headerName: "Email Address",
        field: "email",
      },
      {
        headerName: "Phone Number",
        field: "phoneString",
      },
      {
        headerName: "Status",
        field: "status",
      },
      {
        headerName: "Date Created",
        field: "dateCreateObject",
        type: "dateTime",
      },
    ],
    []
  );

  useEffect(() => {
    dataLayer.GetNetwork();
    setMetrics([
      {
        title: "ACTIVE ALARMS",
        value: "-",
      },
      {
        title: "ORGANIZATIONS",
        value: "-",
      },
      {
        title: "LOCATIONS",
        value: "-",
      },
      {
        title: "TANKS",
        value: "-",
      },
      {
        title: "USERS",
        value: "-",
      },
    ]);
  }, []);

  useEffect(() => {
    //Set Bread Crumbs
    dispatch(
      setBreadCrumbs([
        {
          title: "Admin Networks",
          link: "/admin/networks",
        },
        {
          title: "...",
          link: ``,
        },
      ])
    );
  }, []);

  useEffect(() => {
    //Set Bread Crumbs
    if (network?.name) {
      dispatch(
        setBreadCrumbs([
          {
            title: "Admin Networks",
            link: "/admin/networks",
          },
          {
            title: `${network.name}`,
            link: `/admin/networks/${networkId}`,
          },
        ])
      );
    }
  }, [network]);

  const dataLayer = {
    GetNetwork: async () => {
      setLoading(true);
      const { network, metrics: _metrics } = await API.Network.NetworkGet({ networkId });
      network.users.forEach((user) => {
        user.status = (() => {
          if (user.deleted) {
            return "Deactivated";
          } else if (user.invitePending) {
            return "Invite Pending";
          } else {
            return "Active";
          }
        })();
        user.dateCreateObject = new Date(user.created);
        user.phoneString = FormatPhoneNumber(user.phone);
      });
      setNetwork(network);
      setMetrics([
        {
          title: "ACTIVE ALARMS",
          value: _metrics?.activeAlarmsCount || 0,
        },
        {
          title: "ORGANIZATIONS",
          value: _metrics?.organizationsCount || 0,
        },
        {
          title: "LOCATIONS",
          value: _metrics?.locationsCount || 0,
        },
        {
          title: "TANKS",
          value: _metrics?.tanksCount || 0,
        },
        {
          title: "USERS",
          value: _metrics?.usersCount || 0,
        },
      ]);
      setLoading(false);
      setReloadData(true);
    },
  };

  const handlers = {
    editNetworkClick: () => {
      navigate(`/admin/networks/${networkId}/edit`);
    },
  };

  return (
    <>
      
      <MetricBoxesComponent title={""} metrics={metrics} />
      <PaddingWrapper>
        <Card>
          <TableHeader>
            <div>
              <h2>Network Organizations</h2>
              <TableSubHeader>View all Organization in Network</TableSubHeader>
            </div>

            <Button
              onClick={handlers.editNetworkClick}
              variant="contained"
              color="primary"
              size="large"
            >
              EDIT NETWORK
            </Button>
          </TableHeader>
          <Table
            columns={tableColumns}
            label={""}
            isAdminView={true}
            reloadData={reloadData}
            loadingOverride={loading}
            placeholder="There are no Organizations in Network"
            getRowsFunction={async () => {
              return network?.organizations || [];
            }}
            initialState={{
              pinnedColumns: { left: ["name"] },
              sorting: {
                sortModel: [{ field: "name", sort: "asc" }],
              },
            }}
          />
        </Card>
      </PaddingWrapper>
      <PaddingWrapper>
        <Card>
          <TableHeader>
            <div>
              <h2>Network Users</h2>
              <TableSubHeader>
                To add or remove Users from a Network go to the{" "}
                <a href="/admin/users">Users Page</a>
              </TableSubHeader>
            </div>
          </TableHeader>

          <TableComponent
            columns={userTableColumns}
            label={""}
            isAdminView={true}
            reloadData={reloadData}
            loadingOverride={loading}
            placeholder="There are no users in this nework"
            getRowsFunction={async () => {
              return network?.users || [];
            }}
            initialState={{
              sorting: {
                sortModel: [{ field: "name", sort: "asc" }],
              },
            }}
          />
        </Card>
      </PaddingWrapper>
    </>
  );
};

export default NetworkDashboard;
